import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Search } from "lucide-react";

import Typography from "@/components/ui/typography";

import { useSearchStatus } from "../hooks/useLeads";
import { LeadSearch } from "../types/leads";

interface ActiveSearchBannerProps {
  search: LeadSearch;
  onComplete: () => void;
}

const ActiveSearchBanner = ({
  search,
  onComplete,
}: ActiveSearchBannerProps) => {
  const { data: statusData } = useSearchStatus(search.id);
  const [dismissed, setDismissed] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  // Detect completion from polling
  useEffect(() => {
    if (statusData?.status === "completed" && !justCompleted) {
      setJustCompleted(true);
      onComplete();
    }
  }, [statusData?.status, justCompleted, onComplete]);

  // Auto-dismiss after 5 seconds of completion
  useEffect(() => {
    if (!justCompleted) return;
    const timeout = setTimeout(() => setDismissed(true), 5000);
    return () => clearTimeout(timeout);
  }, [justCompleted]);

  if (dismissed || statusData?.status === "failed") return null;

  return (
    <div
      className={`rounded-xl border px-4 py-3.5 transition-all duration-500 ${
        justCompleted
          ? "border-sds-green-40"
          : "border-sds-purple-40"
      }`}
    >
      <div className="flex items-center gap-3">
        {justCompleted ? (
          <CheckCircle2 className="h-4.5 w-4.5 text-sds-green-100 flex-shrink-0" />
        ) : (
          <Loader2 className="h-4.5 w-4.5 text-sds-purple-80 animate-spin flex-shrink-0" />
        )}

        <Typography
          size="b2"
          className={`text-sm flex-1 min-w-0 truncate ${
            justCompleted
              ? "text-sds-green-100 font-semibold"
              : "text-[#494949]"
          }`}
        >
          {justCompleted ? (
            <>
              Search complete — {statusData?.totalMatched ?? 0} leads found
              {(statusData?.duplicatesSkipped ?? 0) > 0
                ? `, ${statusData!.duplicatesSkipped} duplicates skipped`
                : null}
              , {statusData?.creditsCharged ?? 0} credits used
            </>
          ) : (
            <>
              <span className="font-semibold">Searching for leads</span>
              <span className="text-[#767677]">
                {" — "}
                <Search className="h-3 w-3 inline" />{" "}
                {search.query.length > 80
                  ? search.query.slice(0, 80) + "..."
                  : search.query}
                {statusData?.totalMatched ? (
                  <span className="ml-2 text-[#a4a4a4]">
                    ({statusData.totalMatched} matched so far)
                  </span>
                ) : null}
              </span>
            </>
          )}
        </Typography>

        {justCompleted && (
          <button
            onClick={() => setDismissed(true)}
            className="text-xs text-[#767677] hover:text-[#494949] transition-colors flex-shrink-0"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

export default ActiveSearchBanner;
